import { Injectable } from '@angular/core';

import { EMPTY, expand, finalize, map, Observable, of, reduce, tap } from 'rxjs';

import { EmailMessage, PagedResponse, Team } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get and save team data.
 */
@Injectable()
export class TeamService {
    public static INCLUDE_COACHES: string = 'coaches';
    public static INCLUDE_RESULT_EMAILS: string = 'resultEmails';

    private cachedTeamsMap: { [apiRequest: string]: Team[]; } = {};
    private cachedTeamMap: { [apiRequest: string]: Team; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    public clearCache(): void {
        this.cachedTeamsMap = {};
        this.cachedTeamMap = {};
    }

    /**
     * Get all teams for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(eventId, apiRequestUrl);
    }

    /**
     * Get teams by a url. This is used for paging through teams since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(eventId: string, url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const teams: Team[] = [];
                for (const team of json.data) {
                    teams.push((new Team(eventId)).deserialize(team, json.included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    teams,
                );
            }));
    }

    /**
     * Get all teams for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null, queryParams: string[] = null): Observable<Team[]> {
        queryParams = queryParams || [];

        if (queryParams.filter((x: string) => x.indexOf('page[size]') !== -1).length === 0) {
            queryParams.push('page[size]=500');
        }

        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        if (this.cachedTeamsMap[apiRequestUrl]) {
            // if `cachedTeams` is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedTeamsMap[apiRequestUrl].map((x: Team) => x.clone()),
            );
        }
        // Make the http request
        // Recursively get all teams in this event
        return this.http.get(apiRequestUrl)
            .pipe(
                expand((response: IJsonApiResponse) => {
                    const json: Object = response;

                    if (json['links'] && json['links']['next']) {
                        // If you have more values we need to call http again. This is done recursively
                        return this.http.get(json['links']['next'], false, false);
                    }
                    return EMPTY;
                }),
                map((response: IJsonApiResponse) => {
                    const teams: Team[] = [];
                    const included: Object[] = response.included;
                    const bodyData: any = response.data || [];
                    for (const team of bodyData) {
                        teams.push((new Team(eventId)).deserialize(team, included));
                    }

                    return teams;
                }),
                reduce((previousValue: any, currentValue: any) => previousValue.concat(currentValue)),
                tap((returnData: Team[]) => {
                    this.cachedTeamsMap[apiRequestUrl] = returnData.map((x: Team) => x.clone());
                    return this.cachedTeamsMap[apiRequestUrl];
                }),
            );
    }

    /**
     * Get a single team's data
     */
    public get(
        eventId: string,
        teamId: string,
        includes: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<Team> {
        const queryParams: string[] = this.appendGetFields();
        const apiRequestUrl: string = this.getApiUrl(eventId, teamId, includes, queryParams);

        if (!forceRefresh &&
            this.cachedTeamMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTeamMap[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const team: Team = (new Team(eventId).deserialize(response));
                this.cachedTeamMap[apiRequestUrl] = team.clone();
                return team;
            }));
    }

    /**
     * Get a team's registration password
     */
    public getRegistrationPassword(eventId: string, teamId: string, includes: string[] = null): Observable<string> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, teamId, includes)}/registration-password`;
        return this.http.get<string>(apiRequestUrl, false).pipe(map((response: string) => response));
    }

    public exportToCsv(eventId: string, queryParams: string[] = null): void {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/teams/csv-download`;
        url += this.http.getQueryParameters(null, queryParams);
        location.href = url;
    }

    /**
     * Add or update the team
     */
    public save(
        team: Team,
        includes: string[] = null,
        generateRegistrationPassword: boolean = false,
        queryParams: string[] = null,
        isHomeTeam: boolean = false,
    ): Observable<Team> {
        this.clearCache();
        let response: Observable<IJsonApiResponse>;
        queryParams = this.appendGetFields(queryParams);
        const teamId: string = team.isNew ? null : team.id;
        const apiRequestUrl: string = this.getApiUrl(team.eventId, teamId, includes, queryParams);
        let teamSerialized: string = team.serialize();

        if (generateRegistrationPassword) {
            const teamJson: any = JSON.parse(teamSerialized);
            teamJson.data.attributes.generateRegistrationPassword = true;
            teamSerialized = JSON.stringify(teamJson);
        }

        if (isHomeTeam) {
            const teamJson: any = JSON.parse(teamSerialized);
            teamJson.data.attributes.isHomeTeam = true;
            teamSerialized = JSON.stringify(teamJson);
        }

        if (!team.isNew) {
            // Update existing Team
            response = this.http.patch(apiRequestUrl, teamSerialized);
        } else {
            // Add a new Team
            response = this.http.post(apiRequestUrl, teamSerialized);
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new Team(team.eventId)).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified team.
     */
    public delete(eventId: string, teamId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, teamId, null);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Email team invites to the given list of teamCoachIds
     */
    public emailTeamInvites(eventId: string, toTeamCoachIds: string[]): Observable<{ sentCount: number }> {
        const apiRequestUrl: string = this.getApiUrl(eventId, 'email-team-invites/');

        const payload: any = {};
        payload.data = {};
        payload.data.type = 'team';
        payload.data.attributes = {};
        payload.data.attributes.toTeamContactIds = toTeamCoachIds;

        return this.http.post<ITeamInvite>(apiRequestUrl, JSON.stringify(payload))
            .pipe(map((response: ITeamInvite) => response));
    }

    /**
     * Get the team invite email template
     */
    public getTeamInviteEmail(eventId: string): Observable<EmailMessage> {
        const apiRequestUrl: string = this.getApiUrl(eventId, 'team-invite-email');

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const email: EmailMessage = new EmailMessage();
                email.fromName = json.fromName;
                email.fromEmail = json.fromEmail;
                email.subject = json.subject;
                email.body = json.body;
                return email;
            }));
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[team]') !== -1).length === 0) {
            queryParams.push(`fields[team]=${Team.getFields().join(',')}`);
        }

        return queryParams;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        teamId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/teams/`;

        if (teamId) {
            url += teamId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface ITeamInvite {
    sentCount: number;
}
